// pages/Dashboard/Services.js
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  FaSearch,
  FaPlus,
  FaCut,
  FaTimes,
  FaClock,
  FaRupeeSign,
  FaTags,
  FaEdit,
  FaTrash,
  FaLayerGroup,
  FaFire,
  FaToggleOn,
  FaToggleOff,
} from 'react-icons/fa';
import '../Css/Services.css';

import { useToast } from '../../components/Toast';
import { baseurl } from '../Baseurl';

const CATEGORY_STYLES = {
  Hair: { cls: 'cat-hair' },
  Skin: { cls: 'cat-skin' },
  Nails: { cls: 'cat-nails' },
  Makeup: { cls: 'cat-makeup' },
  Spa: { cls: 'cat-spa' },
};

const FILTERS = [
  { key: 'all', label: 'All Services' },
  { key: 'Hair', label: 'Hair' },
  { key: 'Skin', label: 'Skin' },
  { key: 'Nails', label: 'Nails' },
  { key: 'Makeup', label: 'Makeup' },
  { key: 'Spa', label: 'Spa' },
];

const EMPTY_FORM = {
  name: '',
  category: '',
  duration: '',
  price: '',
  description: '',
  status: 'active',
};

function Services() {
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState({ totalServices: 0, activeServices: 0, totalCategories: 0, mostBooked: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedService, setSelectedService] = useState(null);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);


  const [editingId, setEditingId] = useState(null);

 
const toast = useToast();

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });

  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (activeFilter !== 'all') params.append('category', activeFilter);

      const res = await fetch(`${baseurl}/services?${params.toString()}`, {
        headers: authHeaders(),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || 'Failed to fetch services');

      setServices(data.data.services);
      setStats(data.data.stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, activeFilter]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const filteredServices = useMemo(() => services, [services]);

const openAddDrawer = () => {
  setForm(EMPTY_FORM);
  setEditingId(null);
  setEditMode(false);
  setShowAddDrawer(true);
};

  const openEditDrawer = (service) => {
  setForm({
    name: service.name,
    category: service.category,
    duration: service.duration,
    price: service.price,
    description: service.description || '',
    status: service.status,
  });
  setEditingId(service.id);
  setEditMode(true);
  setSelectedService(null);
  setShowAddDrawer(true);
};

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

const handleSubmit = async () => {
  if (!form.name || !form.category || !form.duration || !form.price) {
    setError('Please fill in all required fields');
    toast('Please fill in all required fields', 'error');
    return;
  }

  setSaving(true);
  setError('');

  try {
    const payload = {
      name: form.name,
      category: form.category,
      duration: Number(form.duration),
      price: Number(form.price),
      description: form.description,
      status: form.status,
    };

    const url = editMode
      ? `${baseurl}/services/${editingId}`
      : `${baseurl}/services`;

    const res = await fetch(url, {
      method: editMode ? 'PUT' : 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to save service');

    toast(data.message || (editMode ? 'Service updated successfully' : 'Service created successfully'), 'success');

    setShowAddDrawer(false);
    setForm(EMPTY_FORM);
    setEditingId(null);
    await fetchServices();
  } catch (err) {
    setError(err.message);
    toast(err.message || 'Failed to save service', 'error');
  } finally {
    setSaving(false);
  }
};

const handleToggleStatus = async (service) => {
  try {
    const res = await fetch(`${baseurl}/services/${service.id}/toggle-status`, {
      method: 'PATCH',
      headers: authHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to toggle status');

    toast(data.message || 'Service status updated', 'success');

    await fetchServices();
    setSelectedService(data.data.service);
  } catch (err) {
    setError(err.message);
    toast(err.message || 'Failed to toggle status', 'error');
  }
};

const handleDelete = async (id) => {
  try {
    const res = await fetch(`${baseurl}/services/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to delete service');

    toast(data.message || 'Service deleted successfully', 'success');

    setSelectedService(null);
    await fetchServices();
  } catch (err) {
    setError(err.message);
    toast(err.message || 'Failed to delete service', 'error');
  }
};

  return (
    <div className="services-page">
      {/* HEADER */}
      <div className="services-header">
        <div>
          <h2>Services</h2>
          <p>Manage service categories, pricing, and duration</p>
        </div>
        <button className="btn-primary" onClick={openAddDrawer}>
          <FaPlus /> Add Service
        </button>
      </div>

      {error && <div className="services-error-banner">{error}</div>}

      {/* STATS STRIP */}
      <div className="stats-strip">
        <div className="stat-card">
          <span className="stat-value">{stats.totalServices}</span>
          <span className="stat-label">Total Services</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.activeServices}</span>
          <span className="stat-label">Active Services</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.totalCategories}</span>
          <span className="stat-label">Categories</span>
        </div>
        <div className="stat-card">
          <span className="stat-value stat-value-sm">{stats.mostBooked?.name || '-'}</span>
          <span className="stat-label">Most Booked</span>
        </div>
      </div>

      {/* SEARCH + FILTERS */}
      <div className="services-toolbar">
        <div className="input-group search-input">
          <FaSearch className="input-icon" />
          <input
            type="text"
            placeholder="Search services by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="filter-chips">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`filter-chip ${activeFilter === f.key ? 'active' : ''}`}
              onClick={() => setActiveFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* SERVICES TABLE */}
      <div className="services-table-wrapper">
        <table className="services-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Category</th>
              <th>Duration</th>
              <th>Price</th>
              <th>Bookings</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="7" className="services-empty-row">
                  Loading services...
                </td>
              </tr>
            )}

            {!loading &&
  filteredServices.map((service) => (
    <tr key={service.id} className="service-row">
      <td>
        <div className="table-service-name">
          {/* <div className="service-icon-box">
            <FaCut />
          </div> */}
          <div>
            <strong>{service.name}</strong>
            <span className="table-sub">{service.bookings} bookings</span>
          </div>
        </div>
      </td>
      <td>
        <span className={`category-tag ${CATEGORY_STYLES[service.category]?.cls}`}>
          {service.category}
        </span>
      </td>
      <td>
        <span className="table-duration">
          <FaClock /> {service.duration} min
        </span>
      </td>
      <td>
        <span className="table-price">₹{Number(service.price).toLocaleString()}</span>
      </td>
      <td>{service.bookings}</td>
      <td>
        {/* <span
          className={`status-pill ${service.status === 'active' ? 'status-active' : 'status-inactive'}`}
          onClick={() => handleToggleStatus(service)}
        >
          {service.status === 'active' ? <FaToggleOn /> : <FaToggleOff />}
          {service.status === 'active' ? 'Active' : 'Inactive'}
        </span> */}


        <span
          className={`status-pill ${service.status === 'active' ? 'status-active' : 'status-inactive'}`}
         
        >
          
          {service.status === 'active' ? 'Active' : 'Inactive'}
        </span>
         
      </td>
      <td>
        <button
          className="table-view-btn"
          onClick={() => setSelectedService(service)}
        >
          View
        </button>
      </td>
    </tr>
  ))}

            {!loading && filteredServices.length === 0 && (
              <tr>
                <td colSpan="7" className="services-empty-row">
                  No services match your search
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* SERVICE DETAILS DRAWER */}
      {selectedService && (
        <div className="drawer-overlay" onClick={() => setSelectedService(null)}>
          <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-header-info">
                <div className="service-icon-box large">
                  <FaCut />
                </div>
                <div>
                  <h3>{selectedService.name}</h3>
                  <span className={`category-tag ${CATEGORY_STYLES[selectedService.category]?.cls}`}>
                    {selectedService.category}
                  </span>
                </div>
              </div>
              <button className="drawer-close-btn" onClick={() => setSelectedService(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="drawer-body">
              <div className="drawer-stat-row">
                <div className="drawer-stat-box">
                  <FaRupeeSign className="drawer-stat-icon" />
                  <div>
                    <strong>₹{Number(selectedService.price).toLocaleString()}</strong>
                    <span>Price</span>
                  </div>
                </div>
                <div className="drawer-stat-box">
                  <FaClock className="drawer-stat-icon" />
                  <div>
                    <strong>{selectedService.duration} min</strong>
                    <span>Duration</span>
                  </div>
                </div>
                <div className="drawer-stat-box">
                  <FaFire className="drawer-stat-icon" />
                  <div>
                    <strong>{selectedService.bookings}</strong>
                    <span>Bookings</span>
                  </div>
                </div>
              </div>

              <div className="drawer-section">
                <h4>
                  <FaLayerGroup /> Status
                </h4>
                <span
                  className={`status-pill ${selectedService.status === 'active' ? 'status-active' : 'status-inactive'}`}
                  onClick={() => handleToggleStatus(selectedService)}
                >
                  {selectedService.status === 'active' ? <FaToggleOn /> : <FaToggleOff />}
                  {selectedService.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="drawer-section">
                <div className="drawer-section-head">
                  <h4>
                    <FaTags /> Description
                  </h4>
                  <button className="edit-link" onClick={() => openEditDrawer(selectedService)}>
                    <FaEdit /> Edit
                  </button>
                </div>
                <p className="notes-text">{selectedService.description || 'No description added.'}</p>
              </div>
            </div>

            <div className="drawer-footer">
              <button className="btn-outline full-width" onClick={() => openEditDrawer(selectedService)}>
                <FaEdit /> Edit Service
              </button>
              <button className="btn-danger full-width" onClick={() => handleDelete(selectedService.id)}>
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD / EDIT SERVICE DRAWER */}
      {showAddDrawer && (
        <div className="drawer-overlay" onClick={() => setShowAddDrawer(false)}>
          <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-header-info">
                <div className="service-icon-box large">
                  <FaPlus />
                </div>
                <div>
                  <h3>{editMode ? 'Edit Service' : 'Add New Service'}</h3>
                  <span className="table-sub">Create a service under a category</span>
                </div>
              </div>
              <button className="drawer-close-btn" onClick={() => setShowAddDrawer(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="drawer-body">
              <div className="drawer-section">
                <label className="form-label">Service Category</label>
                <select
                  className="form-select"
                  value={form.category}
                  onChange={(e) => handleFormChange('category', e.target.value)}
                >
                  <option value="">Select a category</option>
                  <option value="Hair">Hair</option>
                  <option value="Skin">Skin</option>
                  <option value="Nails">Nails</option>
                  <option value="Makeup">Makeup</option>
                  <option value="Spa">Spa</option>
                </select>
              </div>

              <div className="drawer-section">
                <label className="form-label">Service Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Hair Color - Global"
                  value={form.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                />
              </div>

              <div className="drawer-section form-grid">
                <div>
                  <label className="form-label">Duration (minutes)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g. 60"
                    value={form.duration}
                    onChange={(e) => handleFormChange('duration', e.target.value)}
                  />
                </div>
                <div>
                  <label className="form-label">Price (₹)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g. 1200"
                    value={form.price}
                    onChange={(e) => handleFormChange('price', e.target.value)}
                  />
                </div>
              </div>

              <div className="drawer-section">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  rows="4"
                  placeholder="Briefly describe the service"
                  value={form.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                ></textarea>
              </div>

              <div className="drawer-section">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={form.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="drawer-footer">
              <button className="btn-outline full-width" onClick={() => setShowAddDrawer(false)}>
                Cancel
              </button>
              <button className="btn-primary full-width" onClick={handleSubmit} disabled={saving}>
                <FaPlus /> {saving ? 'Saving...' : editMode ? 'Update Service' : 'Create Service'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Services;